// /datasets/:id/fair — the dataset's FAIR self-check (GET /api/datasets/{id}/fair,
// backend services/fair.py) and its "ready for integration" mark.
//
// Each check names the FAIRsFAIR / F-UJI metric it approximates, a status (pass / warn / fail)
// and the tab where it is fixed. Required checks (provenance) must pass before the dataset's
// editors can mark the dataset ready (POST .../ready; 409 otherwise). Since N6 (2026-10-14) the
// descriptive check warns about the name, the description and every additional field required
// in the project (fixed in the Metadata tab); the creators' ORCID check is gone. The checks and
// their texts are the server's; this tab only lists them.
// Ready is evaluated on read: a mark whose required check fails now is "stale". In the project
// database design a dataset that is not ready is only a warning; it is always built.
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card } from "../../components/Shared";
import { useApi } from "../../useApi";

const STATUS = {
  pass: { color: "green", glyph: "✓", word: "pass" },
  warn: { color: "amber", glyph: "!", word: "warn" },
  fail: { color: "red", glyph: "✗", word: "fail" },
};

export const READY_STATES = {
  ready: { color: "green", label: "✓ ready for integration" },
  stale: { color: "amber", label: "! ready mark stale" },
  not_ready: { color: "gray", label: "– not ready" },
};

const TAB_LABELS = { files: "Files", metadata: "Metadata", columns: "Columns",
                     provenance: "Provenance" };

export function ReadyBadge({ state }) {
  const s = READY_STATES[state];
  if (!s) return null;
  return <Badge color={s.color}>{s.label}</Badge>;
}

function StatusCell({ status }) {
  const s = STATUS[status] || { color: "gray", glyph: "?", word: status };
  return <Badge color={s.color}>{s.glyph} {s.word}</Badge>;
}

export default function FairTab({ dataset, canEdit, reloadDataset }) {
  const fair = useApi(`/datasets/${dataset.id}/fair`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const f = fair.data;

  if (fair.loading && !f) return <Loading />;
  if (fair.error) return <ErrorNote error={fair.error} />;
  if (!f) return null;

  const act = async (call) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      fair.reload();
      reloadDataset();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const failingRequired = f.checks.filter(c => c.required && c.status === "fail");
  const marked = !!f.ready_for_integration_at;

  return (
    <div>
      <Card style={{ marginBottom: 16, padding: "14px 20px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <ReadyBadge state={f.ready_state} />
          <span role="status" style={{ fontSize: 14 }}>
            {f.ready_state === "ready" && (
              <>Marked ready by {f.ready_for_integration_by || "a former user"} on{" "}
                {new Date(f.ready_for_integration_at).toLocaleDateString()}.</>
            )}
            {f.ready_state === "stale" && (
              <>Marked ready by {f.ready_for_integration_by || "a former user"}, but a required check fails
                now. Fix it below; the mark counts again as soon as it passes.</>
            )}
            {f.ready_state === "not_ready" && (
              failingRequired.length
                ? <>Not ready: {failingRequired.map(c => c.title.toLowerCase()).join(", ")} must pass first.</>
                : <>Every required check passes; the dataset can be marked ready for integration.</>
            )}
          </span>
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>
          The project database is built from every included dataset; one that is not ready shows a
          warning in the data check of the database design.
        </p>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Btn disabled={busy || !f.can_mark_ready}
                 onClick={() => act(() => api.post(`/datasets/${dataset.id}/ready`))}>
              {marked ? "Mark ready again" : "Mark ready for integration"}
            </Btn>
            {marked && (
              <Btn variant="secondary" disabled={busy}
                   onClick={() => act(() => api.del(`/datasets/${dataset.id}/ready`))}>
                Clear the ready mark
              </Btn>
            )}
          </div>
        )}
        <ErrorNote error={error} />
      </Card>

      <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <caption style={{ textAlign: "left", fontWeight: 600, marginBottom: 8 }}>FAIR self-check</caption>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>
            <th scope="col" style={{ padding: "6px 8px" }}>Status</th>
            <th scope="col" style={{ padding: "6px 8px" }}>Check</th>
            <th scope="col" style={{ padding: "6px 8px" }}>Finding</th>
            <th scope="col" style={{ padding: "6px 8px" }}>Fix in</th>
          </tr>
        </thead>
        <tbody>
          {f.checks.map(c => (
            <tr key={c.id} style={{ borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
              <td data-label="Status" style={{ padding: "8px" }}><StatusCell status={c.status} /></td>
              <td data-label="Check" style={{ padding: "8px" }}>
                <div style={{ fontWeight: 600 }}>{c.title}</div>
                <div style={{ fontSize: 12, color: "#4b5563" }}>
                  {c.metric || "local check"}{c.required ? " · required" : ""}
                </div>
              </td>
              <td data-label="Finding" style={{ padding: "8px" }}>{c.message}</td>
              <td data-label="Fix in" style={{ padding: "8px" }}>
                {c.status !== "pass" && (
                  <Link to={`/datasets/${dataset.id}/${c.fix.tab}`}>{TAB_LABELS[c.fix.tab] || c.fix.tab}</Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
